import React from "react";
import { motion } from "framer-motion";
import Navbar from "../../../DashBoardComponents/Navbar";
import {
  FileText,
  FolderHeart,
  Stethoscope,
  FlaskConical,
  Pill,
  Activity,
  ShieldCheck,
  Check,
  Building2,
  UserRound,
  Network,
  ArrowRight,
  Search,
  Upload,
  Share2,
  Lock,
  RefreshCcw,
  ClipboardList,
} from "lucide-react";

// ============================================================
// ANIMATION VARIANTS — same pattern as Telemedicine page
// ============================================================

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -45 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 45 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const viewport = {
  once: true,
  amount: 0.18,
};

export default function HealthRecords() {
  const recordTypes = [
    {
      icon: FileText,
      title: "Consultation History",
      desc: "Keep relevant consultation information connected to the patient's ongoing care journey.",
    },
    {
      icon: FlaskConical,
      title: "Lab Reports",
      desc: "Keep relevant diagnostic reports and test information organised and easier to access.",
    },
    {
      icon: Pill,
      title: "Prescriptions",
      desc: "Keep digital prescription information connected to the appropriate healthcare workflow.",
    },
    {
      icon: Activity,
      title: "Health Information",
      desc: "Keep relevant health information available alongside other parts of the care journey.",
    },
    {
      icon: ClipboardList,
      title: "Medical Documents",
      desc: "Keep relevant healthcare documents organised so they can be available when needed.",
    },
    {
      icon: RefreshCcw,
      title: "Care History",
      desc: "Support continuity by keeping relevant information connected across consultations and follow-ups.",
    },
  ];

  const benefitsLeft = [
    "Keep relevant health information organised",
    "Reduce the need to search across different documents",
    "Make relevant information easier to access during care",
    "Support continuity across consultations and follow-ups",
  ];

  const benefitsRight = [
    "Connect records with the broader Doxzon care workflow",
    "Support authorised access to relevant patient information",
    "Keep reports and prescriptions connected to care",
    "Help healthcare professionals understand relevant history",
  ];

  const howItWorks = [
    {
      icon: UserRound,
      step: "01",
      title: "Create Your Record",
      desc: "Start building your digital health record through the information supported by the Doxzon platform.",
    },
    {
      icon: Upload,
      step: "02",
      title: "Add Information",
      desc: "Add or receive relevant reports, prescriptions, consultation information and other supported records.",
    },
    {
      icon: FolderHeart,
      step: "03",
      title: "Organise",
      desc: "Keep relevant healthcare information organised as part of your connected health journey.",
    },
    {
      icon: Share2,
      step: "04",
      title: "Share",
      desc: "Make relevant information available to authorised healthcare professionals when appropriate.",
    },
    {
      icon: RefreshCcw,
      step: "05",
      title: "Continue Care",
      desc: "Use connected information to support future consultations, follow-ups and ongoing care.",
    },
  ];

  const patientBenefits = [
    "Keep relevant healthcare information in one connected workflow",
    "Find reports and prescriptions more easily",
    "Share relevant information with authorised professionals",
    "Support continuity between consultations and follow-ups",
    "Spend less time searching for previous healthcare information",
  ];

  const users = [
    { icon: UserRound, label: "Patients" },
    { icon: Stethoscope, label: "Doctors" },
    { icon: Building2, label: "Clinics" },
    { icon: Network, label: "Healthcare Networks" },
    { icon: FlaskConical, label: "Diagnostic Services" },
    { icon: FolderHeart, label: "Care Teams" },
  ];

  const faqs = [
    {
      q: "What are digital health records?",
      a: "Digital health records are electronic healthcare information that can include supported consultation details, reports, prescriptions and other relevant care information.",
    },
    {
      q: "What information can be included in Doxzon Health Records?",
      a: "This depends on the features and integrations available in the Doxzon platform. Supported information can include relevant consultation history, diagnostic reports, prescriptions and other healthcare documents.",
    },
    {
      q: "Can healthcare professionals access my health records?",
      a: "Authorised healthcare professionals may access relevant patient information according to the permissions, workflows and access controls implemented by Doxzon.",
    },
    {
      q: "Can I share my health records?",
      a: "Relevant health information can be shared according to the platform's sharing capabilities, permissions and applicable privacy requirements.",
    },
    {
      q: "Are health records connected to consultations?",
      a: "Where supported by the Doxzon workflow, relevant patient information can remain connected to consultations and follow-up care.",
    },
    {
      q: "Does Doxzon replace a healthcare professional?",
      a: "No. Health Records are designed to support the healthcare journey and information management; they do not replace clinical judgement or professional care.",
    },
  ];

  const popularRecords = [
    "Lab Reports",
    "Prescriptions",
    "Consultation History",
    "Health Documents",
  ];

  return (
    <>
      <Navbar />

      <div className="font-jakarta bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO — same visual pattern as Telemedicine
        ====================================================== */}
        <section className="relative min-h-[720px] bg-gradient-to-br from-cyan-50 via-white to-blue-50 overflow-hidden">

          <motion.div
            className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full bg-teal-300/20 blur-3xl"
            animate={{
              scale: [1, 1.15, 1],
              x: [0, -25, 0],
              y: [0, 25, 0],
            }}
            transition={{
              duration: 9,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          <motion.div
            className="absolute -bottom-48 -left-40 w-[550px] h-[550px] rounded-full bg-cyan-300/20 blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              x: [0, 30, 0],
            }}
            transition={{
              duration: 11,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />

          <div
            className="absolute inset-0 opacity-[0.035]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
              backgroundSize: "45px 45px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">

            {/* LEFT */}
            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-white/80 backdrop-blur px-4 py-2"
              >
                <motion.span
                  className="w-2 h-2 rounded-full bg-teal-500"
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [1, 0.5, 1],
                  }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />

                <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">
                  Health Records
                </span>
              </motion.div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[58px] font-medium tracking-[-0.05em] leading-[1.05] mt-7">
                Your Complete
                <span className="relative block text-teal-500 mt-2">
                  Health Record
                  <motion.span
                    className="absolute left-0 bottom-[-8px] h-[4px] rounded-full bg-cyan-400"
                    initial={{ width: 0 }}
                    animate={{ width: "55%" }}
                    transition={{ delay: 0.8, duration: 0.8 }}
                  />
                </span>
              </h1>

              <p className="text-slate-600 text-lg leading-8 mt-8 max-w-xl">
                Keep relevant health information, reports, prescriptions and
                consultation history connected to your healthcare journey with
                Doxzon.
              </p>

              {/* Search-style record bar — mirrors Telemedicine hero */}
              {/* <div className="mt-9 flex flex-col sm:flex-row items-start gap-2 bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/60 p-2"> */}
                {/* <div className="flex items-center gap-2 flex-1 px-3">
                  <Search size={18} className="text-slate-400 flex-shrink-0" />
                  <input
                    type="text"
                    placeholder="Search health records"
                    className="w-full py-3 text-sm text-slate-700 outline-none placeholder:text-slate-400 bg-transparent"
                  />
                </div> */}

                {/* <motion.button
                  whileHover={{
                    scale: 1.03,
                    boxShadow: "0 15px 35px rgba(20,184,166,0.25)",
                  }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center gap-1.5 bg-slate-950 text-white px-3 py-2.5 rounded-lg font-semibold text-sm whitespace-nowrap">
                  View Records
                  <ArrowRight size={16} />
                </motion.button> */}
              {/* </div> */}

              <div className="flex flex-wrap items-center gap-2 mt-4">
                {/* <span className="text-xs text-slate-400 mr-1">Quick access:</span> */}

                {popularRecords.map((label) => (
                  <button
                    key={label}
                    type="button"
                    className="text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 hover:border-teal-300 hover:text-teal-600 transition"
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-6 mt-8 text-sm text-slate-500">
                <span className="flex items-center gap-2">
                  <ShieldCheck size={17} className="text-teal-500" />
                  Privacy-aware access
                </span>

                <span className="flex items-center gap-2">
                  <Check size={17} className="text-teal-500" />
                  Connected care workflow
                </span>
              </div>
            </motion.div>

            {/* RIGHT — product visual */}
            <motion.div
              variants={fadeRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="relative"
            >
              <motion.div
                className="absolute inset-[-35px] rounded-[40px] border border-teal-200/60"
                animate={{ rotate: 360 }}
                transition={{
                  duration: 25,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />

              <motion.div
                className="absolute inset-[-60px] rounded-[50px] border border-cyan-200/40"
                animate={{ rotate: -360 }}
                transition={{
                  duration: 35,
                  repeat: Infinity,
                  ease: "linear",
                }}
              />

              {/* Main records UI */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1, delay: 0.2 }}
                whileHover={{ scale: 1.015 }}
                className="relative z-10 mx-auto rounded-[30px] overflow-hidden border-[8px] border-white shadow-[0_25px_70px_rgba(15,23,42,0.15)] bg-white"
              >
                <div className="p-4 sm:p-5">

                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                      <p className="text-[9px] uppercase tracking-widest text-slate-400">
                        Health Records
                      </p>
                      <h3 className="text-sm font-bold mt-1">
                        Patient Timeline
                      </h3>
                    </div>

                    <div className="w-8 h-8 rounded-lg bg-teal-50 grid place-items-center">
                      <FolderHeart size={15} className="text-teal-600" />
                    </div>
                  </div>

                  <div className="space-y-2 mt-4">

                    {[
                      {
                        icon: Stethoscope,
                        title: "Consultation History",
                        meta: "Recent healthcare activity",
                      },
                      {
                        icon: FlaskConical,
                        title: "Lab Report",
                        meta: "Diagnostic information",
                      },
                      {
                        icon: Pill,
                        title: "Prescription",
                        meta: "Digital prescription",
                      },
                      {
                        icon: FileText,
                        title: "Medical Document",
                        meta: "Healthcare document",
                      },
                    ].map(({ icon: Icon, title, meta }, index) => (
                      <motion.div
                        key={title}
                        whileHover={{ x: 4 }}
                        className="flex items-center gap-2.5 p-2.5 rounded-lg bg-slate-50 border border-slate-100"
                      >
                        <div className="w-8 h-8 rounded-lg bg-white grid place-items-center flex-shrink-0">
                          <Icon size={14} className="text-teal-600" />
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold truncate">
                            {title}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {meta}
                          </p>
                        </div>

                        <span className="text-[9px] font-semibold text-teal-600">
                          {index === 0 ? "RECENT" : "VIEW"}
                        </span>
                      </motion.div>
                    ))}

                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100">
                    <Lock size={13} className="text-teal-600" />
                    <p className="text-[9px] text-slate-400">
                      Access is subject to permissions and applicable controls.
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Floating card 1 */}
              <motion.div
                className="absolute left-0 lg:left-2 top-10 z-20 bg-white rounded-lg shadow-xl border border-slate-100 p-2 w-36"
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div className="flex items-center gap-1.5">
                  <div className="w-7 h-7 rounded-md bg-teal-50 flex items-center justify-center">
                    <FileText size={13} className="text-teal-600" />
                  </div>

                  <div>
                    <p className="text-[8px] text-slate-400">
                      Health Record
                    </p>
                    <p className="text-[11px] font-bold">
                      Information connected
                    </p>
                  </div>
                </div>

                <div className="mt-1.5 flex items-center gap-1 text-[9px] text-teal-600 font-semibold">
                  <Check size={10} />
                  Care history available
                </div>
              </motion.div>

              {/* Floating card 2 */}
              <motion.div
                className="absolute right-0 lg:right-2 bottom-16 z-20 bg-white rounded-lg shadow-xl border border-slate-100 p-2 w-40"
                animate={{ y: [0, 10, 0] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                <div className="flex items-center gap-1.5">
                  <div className="w-7 h-7 rounded-md bg-cyan-50 flex items-center justify-center">
                    <Share2 size={13} className="text-cyan-600" />
                  </div>

                  <div>
                    <p className="text-[8px] text-slate-400">
                      Access
                    </p>
                    <p className="text-[11px] font-bold">
                      Share when appropriate
                    </p>
                  </div>
                </div>

                <div className="mt-1.5 h-1 bg-slate-100 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"
                    initial={{ width: "0%" }}
                    animate={{ width: "100%" }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 3,
                    }}
                  />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            STATS / JOURNEY
        ====================================================== */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-6xl mx-auto px-6 -mt-10 relative z-20"
        >
          <div className="bg-white rounded-xl border border-slate-100 shadow-xl p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ["01", "Collect"],
                ["02", "Organise"],
                ["03", "Share"],
                ["04", "Continue Care"],
              ].map(([value, label]) => (
                <div key={label} className="text-center">
                  <p className="font-sora text-xl font-bold text-slate-950">
                    {value}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-1">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* =====================================================
            INTRO
        ====================================================== */}
        <section className="py-28">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center"
          >
            <motion.div variants={fadeLeft}>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                The connected record
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-[-0.04em] leading-tight mt-4">
                Your health history
                <span className="text-teal-500"> stays connected.</span>
              </h2>
            </motion.div>

            <motion.div variants={fadeRight}>
              <p className="text-lg text-slate-600 leading-8">
                Health information can come from consultations, diagnostic
                services, prescriptions and other parts of the care journey.
                Doxzon is designed to help keep relevant information organised
                and connected.
              </p>

              <p className="text-lg text-slate-600 leading-8 mt-5">
                This helps patients and authorised healthcare professionals
                access relevant information when it is needed for ongoing care.
              </p>
            </motion.div>
          </motion.div>
        </section>

        {/* =====================================================
            FEATURES
        ====================================================== */}
        <section className="relative bg-[#f6fbfc] py-28 overflow-hidden">
          <motion.div
            className="absolute -right-40 top-20 w-[500px] h-[500px] rounded-full bg-teal-200/20 blur-3xl"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 8, repeat: Infinity }}
          />

          <div className="relative max-w-7xl mx-auto px-6">

            <div className="max-w-2xl">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                Capabilities
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-[-0.04em] mt-4">
                Keep important health information
                <span className="text-teal-500"> connected.</span>
              </h2>
            </div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
              className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-8"
            >
              {recordTypes.map(({ icon: Icon, title, desc }, index) => (
                <motion.div
                  key={title}
                  variants={fadeUp}
                  whileHover={{ y: -8 }}
                  className="group relative bg-white rounded-xl border border-slate-100 p-4 shadow-sm hover:shadow-2xl transition-shadow duration-300"
                >
                  <motion.div
                    whileHover={{ rotate: 8, scale: 1.08 }}
                    className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center group-hover:bg-teal-500 transition-colors"
                  >
                    <Icon
                      size={15}
                      className="text-teal-600 group-hover:text-white transition-colors"
                    />
                  </motion.div>

                  <span className="absolute top-4 right-4 font-mono text-[9px] text-slate-200 group-hover:text-teal-300 transition">
                    0{index + 1}
                  </span>

                  <h3 className="font-sora text-sm font-semibold mt-4">
                    {title}
                  </h3>

                  <p className="text-slate-500 text-[11px] leading-5 mt-1.5">
                    {desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            HOW IT WORKS
        ====================================================== */}
        <section className="py-28">
          <div className="max-w-6xl mx-auto px-6">

            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                How it works
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">
                Five steps.
                <span className="text-teal-500">
                  {" "}One connected record.
                </span>
              </h2>
            </div>

            <div className="relative grid md:grid-cols-5 gap-8 md:gap-4 mt-16">

              <motion.div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-[2px] bg-slate-100 overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-teal-500 to-cyan-500"
                  initial={{ width: "0%" }}
                  whileInView={{ width: "100%" }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.8, ease: "easeInOut" }}
                />
              </motion.div>

              {howItWorks.map(({ icon: Icon, step, title, desc }) => (
                <motion.div
                  key={step}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewport}
                  whileHover={{ y: -7 }}
                  className="relative text-center"
                >
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="relative z-10 mx-auto w-16 h-16 rounded-2xl bg-slate-950 border-4 border-white shadow-lg flex items-center justify-center"
                  >
                    <Icon size={23} className="text-teal-400" />
                  </motion.div>

                  <span className="block text-xs font-mono text-teal-500 tracking-widest mt-5">
                    STEP {step}
                  </span>

                  <h3 className="font-sora text-xl font-bold mt-2">
                    {title}
                  </h3>

                  <p className="text-slate-500 text-sm leading-7 mt-3 max-w-sm mx-auto">
                    {desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            BENEFITS
        ====================================================== */}
        <section className="relative overflow-hidden bg-slate-950 py-28 text-white">
          <motion.div
            className="absolute left-0 right-0 top-1/2 h-px bg-teal-500/20"
            animate={{ opacity: [0.2, 0.7, 0.2] }}
            transition={{ duration: 2, repeat: Infinity }}
          />

          <div className="relative max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-16 items-center">

              <motion.div
                variants={fadeLeft}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400">
                  Why Doxzon
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium leading-tight mt-5">
                  Less searching.
                  <br />
                  Better continuity.
                </h2>

                <p className="text-slate-400 text-lg leading-8 mt-6 max-w-lg">
                  Health Records should support the healthcare workflow — not
                  become another disconnected place where information is stored.
                </p>
              </motion.div>

              <motion.div
                variants={stagger}
                initial="hidden"
                whileInView="visible"
                viewport={viewport}
                className="grid sm:grid-cols-2 gap-3"
              >
                {[...benefitsLeft, ...benefitsRight].map((benefit) => (
                  <motion.div
                    key={benefit}
                    variants={fadeUp}
                    whileHover={{
                      scale: 1.03,
                      backgroundColor: "rgba(255,255,255,0.08)",
                    }}
                    className="flex items-start gap-2 bg-white/5 border border-white/10 rounded-lg p-3"
                  >
                    <div className="w-5 h-5 rounded-full bg-teal-500/15 flex items-center justify-center flex-shrink-0">
                      <Check size={11} className="text-teal-400" />
                    </div>

                    <span className="text-[11px] text-slate-300 leading-5">
                      {benefit}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* =====================================================
            FOR HEALTHCARE PROFESSIONALS
        ====================================================== */}
        <section className="bg-slate-50 py-24">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center"
          >
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                For healthcare professionals
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
                Understand the Patient Journey
              </h2>

              <p className="text-gray-600 mt-6 leading-8">
                Relevant patient information can help healthcare professionals
                understand previous consultations, reports and prescriptions
                when that information is available and they are authorised to
                access it.
              </p>
            </div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="grid grid-cols-2 gap-3"
            >
              {[
                { icon: FileText, label: "Consultation History" },
                { icon: FlaskConical, label: "Lab Reports" },
                { icon: Pill, label: "Prescriptions" },
                { icon: FolderHeart, label: "Patient Information" },
              ].map(({ icon: Icon, label }) => (
                <motion.div
                  key={label}
                  variants={fadeUp}
                  whileHover={{ y: -7, scale: 1.02 }}
                  className="bg-white border border-slate-100 rounded-lg p-3 text-center shadow-sm hover:shadow-lg transition"
                >
                  <motion.div
                    whileHover={{ rotate: 8, scale: 1.08 }}
                    className="w-7 h-7 mx-auto rounded-md bg-gradient-to-br from-teal-500 to-cyan-500 grid place-items-center"
                  >
                    <Icon size={13} className="text-white" />
                  </motion.div>

                  <p className="text-[11px] font-semibold text-slate-800 mt-1.5">
                    {label}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </section>

        {/* =====================================================
            FOR PATIENTS
        ====================================================== */}
        <section className="py-24 bg-gradient-to-r from-cyan-50 to-blue-50 relative overflow-hidden">

          <motion.div
            className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-teal-500/10 blur-3xl"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 7, repeat: Infinity }}
          />

          <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">

            <motion.div
              variants={fadeLeft}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                For patients
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
                Your Health Information
                <br />
                <span className="text-teal-500">When You Need It</span>
              </h2>

              <p className="text-gray-600 mt-6 leading-8">
                Keep relevant information organised so you can spend less time
                searching for old reports or prescriptions and more time
                focusing on your healthcare journey.
              </p>
            </motion.div>

            <motion.ul
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="space-y-2"
            >
              {patientBenefits.map((item) => (
                <motion.li
                  key={item}
                  variants={fadeUp}
                  whileHover={{ x: 6 }}
                  className="flex items-center gap-2.5 bg-white/80 backdrop-blur border border-white rounded-md p-2.5 shadow-sm"
                >
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-teal-500/10 text-teal-600 grid place-items-center">
                    <Check size={12} strokeWidth={2.5} />
                  </span>

                  <span className="text-gray-700 font-medium text-xs">
                    {item}
                  </span>
                </motion.li>
              ))}
            </motion.ul>
          </div>
        </section>

        {/* =====================================================
            WHO CAN USE
        ====================================================== */}
        <section className="bg-cyan-50 py-24 relative overflow-hidden">

          <div
            className="absolute inset-0 opacity-30 pointer-events-none"
            style={{
              backgroundImage:
                "radial-gradient(circle at 20% 20%, rgba(20,184,166,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(6,182,212,0.15), transparent 40%)",
            }}
          />

          <div className="relative max-w-6xl mx-auto px-6 text-center">

            <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
              Who uses Doxzon
            </span>

            <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
              Health Records Across the
              <span className="text-teal-500"> Care Ecosystem</span>
            </h2>

            <p className="text-gray-600 mt-6 max-w-3xl mx-auto leading-8">
              Patients, doctors, clinics and connected healthcare services can
              interact with relevant health information in different ways
              depending on their role and permissions.
            </p>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-10"
            >
              {users.map(({ icon: Icon, label }) => (
                <motion.div
                  key={label}
                  variants={fadeUp}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="group bg-white rounded-lg p-3 shadow-md border border-transparent hover:border-teal-200 hover:shadow-xl transition"
                >
                  <motion.div
                    whileHover={{ rotate: 8, scale: 1.1 }}
                    className="w-8 h-8 mx-auto rounded-md bg-gradient-to-br from-teal-500 to-cyan-500 grid place-items-center"
                  >
                    <Icon size={15} className="text-white" />
                  </motion.div>

                  <h3 className="font-sora font-semibold text-xs text-slate-900 mt-1.5">
                    {label}
                  </h3>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            SECURITY
        ====================================================== */}
        <section className="bg-slate-50 py-24">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewport}
            className="max-w-4xl mx-auto px-6 text-center"
          >
            <motion.div
              whileHover={{ scale: 1.08, rotate: 5 }}
              className="w-12 h-12 mx-auto rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 grid place-items-center shadow-lg"
            >
              <Lock size={20} className="text-white" />
            </motion.div>

            <span className="inline-block text-teal-500 uppercase tracking-widest font-semibold text-xs mt-6">
              Security &amp; privacy
            </span>

            <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-3">
              Healthcare Information Deserves Careful Protection
            </h2>

            <p className="text-gray-600 mt-6 leading-8">
              Doxzon is designed with healthcare information and patient
              privacy in mind. Access to health information should be handled
              according to appropriate permissions, controls and applicable
              requirements.
            </p>
          </motion.div>
        </section>

        {/* =====================================================
            HEALTH RECORDS VS DOCUMENT STORAGE
        ====================================================== */}
        <section className="py-24 bg-white">
          <div className="max-w-5xl mx-auto px-6">

            <div className="text-center max-w-3xl mx-auto">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                Understanding Health Records
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
                More than
                <span className="text-teal-500"> storing documents</span>
              </h2>

              <p className="text-gray-600 mt-6 leading-8">
                A connected health record should help relevant information stay
                associated with the patient's care journey rather than simply
                acting as a folder of disconnected files.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-10">

              {[
                {
                  title: "Health Records",
                  desc: "Relevant healthcare information connected to consultations, reports, prescriptions and ongoing care.",
                },
                {
                  title: "Document Storage",
                  desc: "A place to keep files or documents without necessarily connecting them to the broader care workflow.",
                },
              ].map((item) => (
                <motion.div
                  key={item.title}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={viewport}
                  whileHover={{ y: -6 }}
                  className="bg-slate-50 border border-slate-100 rounded-xl p-4"
                >
                  <h3 className="font-sora text-sm font-semibold text-slate-900">
                    {item.title}
                  </h3>

                  <p className="text-slate-600 text-[11px] leading-5 mt-1.5">
                    {item.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* =====================================================
            FAQ
        ====================================================== */}
        <section className="py-24">
          <div className="max-w-4xl mx-auto px-6">

            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">
                FAQ
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium tracking-tight mt-4">
                Frequently Asked Questions
              </h2>
            </div>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={viewport}
              className="mt-12 space-y-3"
            >
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  variants={fadeUp}
                  whileHover={{ y: -5 }}
                  className="bg-white border border-slate-100 rounded-lg p-3 hover:border-teal-200 hover:shadow-lg transition"
                >
                  <div className="flex items-start gap-2.5">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-teal-500 to-cyan-500 text-white grid place-items-center text-[10px] font-bold">
                      0{index + 1}
                    </span>

                    <div>
                      <h3 className="font-sora text-sm font-semibold text-slate-900">
                        {faq.q}
                      </h3>

                      <p className="text-gray-600 text-xs mt-1.5 leading-5">
                        {faq.a}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* =====================================================
            FINAL CTA
        ====================================================== */}
        <section className="px-6 pb-24">
          <div className="max-w-7xl mx-auto rounded-2xl bg-slate-950 text-white px-5 py-8 md:px-10 md:py-10 text-center">

            <span className="text-xs font-bold uppercase tracking-[0.2em] text-teal-400">
              Connected Healthcare
            </span>

            <h2 className="font-sora text-2xl md:text-3xl font-medium tracking-tight mt-3">
              Keep your health journey
              <span className="text-teal-400"> connected.</span>
            </h2>

            <p className="text-slate-300 max-w-2xl mx-auto mt-3 text-xs leading-5">
              Keep relevant healthcare information organised and connected with
              the care you receive through Doxzon.
            </p>

            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-white text-slate-900 px-5 py-2.5 text-xs font-medium hover:bg-slate-100 transition"
            >
              Explore Health Records
              <ArrowRight size={14} />
            </motion.button>
          </div>
        </section>

      </div>
    </>
  );
}